/**
 * Phase 5.4a (2026-05-15) — Sync engine.
 *
 * Owns the lifecycle of the offline outbox:
 *   • detects online/offline transitions (Web API events + axios probe)
 *   • drains the outbox FIFO when online (idempotency-key on each call)
 *   • exponential backoff retry on transient network errors
 *   • marks actions as `failed` on hard business errors (4xx other than
 *     409/429), notifies subscribers so the UI can surface the problem
 *   • refreshes the offline cache bundle every CACHE_REFRESH_MS
 *
 * Public API:
 *   syncEngine.attach(api)        → bind the axios instance (call once
 *                                   after login, in a top-level effect)
 *   syncEngine.detach()           → cleanup on logout
 *   syncEngine.subscribe(fn)      → fn({online, pending, syncing, lastError})
 *   syncEngine.enqueue(action)    → manually queue an action (returns the
 *                                   stored doc with idempotency_key)
 *   syncEngine.sendNow(action)    → try online first, fallback to outbox
 *   syncEngine.flush()            → force an immediate sync attempt
 *   syncEngine.snapshot()         → current {online, pending, syncing,…}
 */
import {
  addOutboxAction,
  countOutbox,
  listOutbox,
  setMeta,
  updateOutboxAction,
  cacheArticles,
  cacheClients,
  cacheSettings,
} from './offlineDB';

const POLL_MS = 5 * 60 * 1000;          // online check every 5 min
const CACHE_REFRESH_MS = 5 * 60 * 1000; // cache bundle refresh every 5 min
const BACKOFF_STEPS = [1000, 2000, 4000, 8000, 15000, 30000];

const uuidv4 = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback (older Safari).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

class SyncEngine {
  constructor() {
    this._api = null;
    this._listeners = new Set();
    this._state = {
      online: typeof navigator !== 'undefined' ? navigator.onLine : true,
      pending: 0,
      syncing: false,
      lastError: null,
      lastSyncAt: null,
    };
    this._pollTimer = null;
    this._cacheTimer = null;
    this._draining = false;
    this._onOnline = () => {
      this._setState({ online: true, lastError: null });
      // Defer to next tick so the state update settles before drain starts.
      setTimeout(() => this.flush(), 0);
    };
    this._onOffline = () => this._setState({ online: false });
  }

  // ── Lifecycle ─────────────────────────────────────────────────────
  attach(api) {
    if (this._api === api) return;
    this._api = api;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this._onOnline);
      window.addEventListener('offline', this._onOffline);
    }
    this._pollTimer = setInterval(() => this.flush(), POLL_MS);
    this._cacheTimer = setInterval(() => this._refreshCache(), CACHE_REFRESH_MS);
    // Initial pass after a tick so React mount completes first.
    setTimeout(() => { this.flush(); this._refreshCache(); }, 800);
  }

  detach() {
    this._api = null;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this._onOnline);
      window.removeEventListener('offline', this._onOffline);
    }
    if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; }
    if (this._cacheTimer) { clearInterval(this._cacheTimer); this._cacheTimer = null; }
  }

  // ── Subscribers ───────────────────────────────────────────────────
  subscribe(fn) {
    this._listeners.add(fn);
    fn(this.snapshot());
    return () => this._listeners.delete(fn);
  }

  snapshot() { return { ...this._state }; }

  _setState(patch) {
    this._state = { ...this._state, ...patch };
    this._notify();
    return true;
  }

  _notify() {
    const snap = this.snapshot();
    this._listeners.forEach((fn) => { try { fn(snap); } catch (_) { /* noop */ } });
  }

  async _refreshPendingCount() {
    try {
      const n = await countOutbox('pending') + await countOutbox('syncing');
      this._setState({ pending: n });
    } catch (_) { /* noop */ }
  }

  // ── Public actions ────────────────────────────────────────────────
  async enqueue({ method, url, body }) {
    const doc = await addOutboxAction({
      idempotency_key: uuidv4(),
      method, url, body,
    });
    await this._refreshPendingCount();
    return doc;
  }

  /**
   * Send-or-queue. Tries the network first when online; on failure (network
   * error or timeout) it falls back to the outbox. Business errors (4xx
   * other than 408/429) are surfaced to the caller as-is.
   *
   * Returns `{ data, offline: false }` on a real server response or
   * `{ data: { __offline: true, idempotency_key, ...echo }, offline: true }`
   * on a queued action so the UI can confirm to the user.
   */
  async sendNow({ method, url, body, headers: extraHeaders = {} }) {
    const key = uuidv4();
    if (!this._api) throw new Error('syncEngine not attached');
    const tryOnline = this._state.online && (typeof navigator === 'undefined' || navigator.onLine);
    if (tryOnline) {
      try {
        const res = await this._api.request({
          method, url, data: body,
          headers: { 'Idempotency-Key': key, ...extraHeaders },
        });
        return { data: res.data, offline: false, idempotency_key: key };
      } catch (err) {
        // Network-level failures → queue. Anything else → throw.
        const isNetwork =
          !err.response &&
          (err.code === 'ERR_NETWORK' ||
            err.message === 'Network Error' ||
            err.code === 'ECONNABORTED' ||
            err.code === 'ETIMEDOUT');
        if (!isNetwork) throw err;
        // Fall through to queue.
        this._setState({ online: false });
      }
    }
    const doc = await addOutboxAction({
      idempotency_key: key,
      method, url, body,
    });
    await this._refreshPendingCount();
    return {
      data: { __offline: true, idempotency_key: key, queued_id: doc.id },
      offline: true,
      idempotency_key: key,
    };
  }

  async flush() {
    if (!this._api || this._draining) return;
    if (!(typeof navigator === 'undefined' || navigator.onLine)) {
      this._setState({ online: false });
      // Still refresh the pending count so the banner reflects newly-queued items.
      await this._refreshPendingCount();
      return;
    }
    this._draining = true;
    this._setState({ syncing: true });
    try {
      const pending = await listOutbox('pending');
      for (const action of pending) {
        await this._drainOne(action);
      }
    } finally {
      this._draining = false;
      this._setState({ syncing: false, lastSyncAt: Date.now() });
      await this._refreshPendingCount();
    }
  }

  async _drainOne(action) {
    await updateOutboxAction(action.id, { status: 'syncing' });
    const attempt = action.retry_count || 0;
    try {
      const res = await this._api.request({
        method: action.method,
        url: action.url,
        data: action.body,
        headers: { 'Idempotency-Key': action.idempotency_key },
      });
      await updateOutboxAction(action.id, {
        status: 'done',
        response: res.data,
        completed_at: Date.now(),
      });
      this._setState({ online: true, lastError: null });
    } catch (err) {
      const status = err.response?.status;
      const isNetwork =
        !err.response &&
        (err.code === 'ERR_NETWORK' || err.message === 'Network Error' ||
         err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT');
      // Transient → keep pending, increment retry, wait, requeue later.
      if (isNetwork || status === 408 || status === 429 || (status >= 500 && status < 600)) {
        const next = Math.min(attempt + 1, BACKOFF_STEPS.length - 1);
        const wait = BACKOFF_STEPS[next];
        await updateOutboxAction(action.id, {
          status: 'pending',
          retry_count: next,
          last_error: err.message || String(err),
          next_attempt_at: Date.now() + wait,
        });
        this._setState({ online: !isNetwork, lastError: err.message || 'network' });
        // Don't continue draining — wait for the next flush.
        throw err;
      }
      // Hard business error (400/401/403/404/409/422) → mark failed.
      const detail = err.response?.data?.detail || err.message || 'erreur métier';
      await updateOutboxAction(action.id, {
        status: 'failed',
        retry_count: attempt + 1,
        last_error: typeof detail === 'string' ? detail : JSON.stringify(detail),
        failed_at: Date.now(),
        failure_status: status,
      });
      this._setState({ lastError: typeof detail === 'string' ? detail : 'erreur' });
    }
  }

  async _refreshCache() {
    if (!this._api) return;
    if (!(typeof navigator === 'undefined' || navigator.onLine)) return;
    try {
      const since = (await import('./offlineDB')).then; // unused — kept simple
      const res = await this._api.get('/cache/sync-bundle');
      if (res?.data) {
        if (Array.isArray(res.data.articles)) await cacheArticles(res.data.articles);
        if (Array.isArray(res.data.clients)) await cacheClients(res.data.clients);
        if (res.data.settings && typeof res.data.settings === 'object') {
          await cacheSettings(res.data.settings);
        }
        await setMeta('last_cache_sync', res.data.server_time || new Date().toISOString());
      }
    } catch (err) {
      // Silent — the user is offline or the bundle endpoint is slow. The
      // previous cache stays valid.
      this._setState({ lastError: err.message || 'cache refresh failed' });
    }
  }

  // ── Manual triggers (UI buttons) ──────────────────────────────────
  async retryFailed() {
    const failed = await listOutbox('failed');
    for (const a of failed) {
      await updateOutboxAction(a.id, { status: 'pending', last_error: null });
    }
    await this._refreshPendingCount();
    await this.flush();
  }
}

const syncEngine = new SyncEngine();
export default syncEngine;
export { uuidv4 };
